import React, { useState } from "react";
import { useSelector } from "react-redux";
import dragIcon from "svg/drag-icon.svg";
import CustomTooltip from "common/tooltip";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { currencyValue, laborItemsHeaders, laborItemsHeadersEditable } from "../../utils";
import LaborCodesEditModal from "../modals/LaborCodesEditModal";
import { api } from "store/slices/api";
import { useParams } from "react-router";
import deleteIcon from "svg/delete_icon.svg";
import Modal from "common/modal";
import { toast } from "react-toastify";
import "../style.scss";

const DeleteLaborCodeModal = ({ laborCodeId, laborCode }) => {
  const [show, setShow] = useState(false);
  const [deleteLaborCode] = api.useDeleteFloorLaborCodeMutation();

  return (
    <td className="table-root-floor__body__tr__td table-root-floor__body__tr__td__text actions-align">
      <img src={deleteIcon} className="actions-align" alt="delete-icon" onClick={() => setShow(true)} />
      {show && (
        <Modal
          title="Delete"
          saveButtonText="Delete"
          closeButtonText="Cancel"
          handleSave={() => {
            toast.promise(deleteLaborCode(laborCodeId).unwrap(), {
              success: "Labor deleted successfully",
              pending: "Removing labor item",
              error: "Something went wrong",
            });
            setShow(false);
          }}
          handleCancel={() => setShow(false)}
          saveButtonClass="removeBtnClass"
          rootClass="transparent-backdrop"
          bodyClass="scroll-bar"
          size="lg"
        >
          <p className="confirmation-text">Are you sure you want to delete {laborCode}?</p>
        </Modal>
      )}
    </td>
  );
};

const tdClass = "table-root-floor__body__tr__td table-root-floor__body__tr__td__text";

export const DraggableLaborRow = ({ row }) => {
  const { quoteId, floorId } = useParams();
  const { storeCcn, terrCcn } = useSelector((state) => state.loggedInUser);
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: row.rank });
  const { data: quote = {} } = api.useGetPriceQuoteQuery(quoteId);
  const [updateLaborCode] = api.useUpdateFloorLaborCodeMutation();

  const canEdit =
    (quote.territory === terrCcn && quote.territory !== "800100") ||
    (storeCcn === quote.storeNumber && quote.punchoutPublished !== true);
  const headers = canEdit ? laborItemsHeadersEditable : laborItemsHeaders;
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 };

  const handleLaborRowSelection = () => {
    const { laborCodeId, extendedPrice } = row;
    const isTaxChecked = !row.isTaxChecked;

    const taxRate = parseFloat(quote.taxRate) / 100;
    const extendedPriceInCents = Math.round(extendedPrice * 100);
    const salesTaxInCents = isTaxChecked ? Math.round(extendedPriceInCents * taxRate) : 0;
    const totalInCents = extendedPriceInCents + salesTaxInCents;

    const payload = { laborCodeId, floorId, isTaxChecked, salesTax: salesTaxInCents / 100, total: totalInCents / 100 };
    toast.promise(updateLaborCode(payload).unwrap(), {
      success: "Labor item updated",
      pending: "Updating labor item",
      error: "Something went wrong",
    });
  };

  const displayRowItem = (row, col) => {
    if (col.name && col.name === "Description") {
      return (
        <CustomTooltip placement="top" text={row[col.dataKey]}>
          {row[col.dataKey]}
        </CustomTooltip>
      );
    }
    if (col.name && (col.name === "Extended Price" || col.name === "Sales Tax" || col.name === "Total")) {
      return currencyValue(row[col.dataKey]);
    } else {
      return row[col.dataKey];
    }
  };

  return (
    <tr ref={setNodeRef} style={style} className="table-root-floor__body__tr">
      {headers.map((col) => {
        if (col.name === "Checkbox") {
          return (
            <td
              key={col.id}
              className="table-root-floor__body__tr__td table-root-floor__body__tr__td__checkbox product pe-3"
            >
              <input
                type="checkbox"
                aria-label="Checkbox selection of rows"
                className="table-root-floor__body__tr__td__checkbox--input"
                checked={row.isTaxChecked}
                onChange={handleLaborRowSelection}
                disabled={!canEdit}
              />
            </td>
          );
        }
        if (col.name === "Labor Code") {
          return (
            <td key={col.id} className={tdClass}>
              {canEdit && (
                <span {...attributes} {...listeners} style={{ cursor: "grab" }} aria-label="Drag to reorder">
                  <img src={dragIcon} alt="Drag" />
                </span>
              )}
              <span style={{ marginLeft: "5px" }}>{displayRowItem(row, col)}</span>
            </td>
          );
        }
        if (col.name === "Edit") {
          return <LaborCodesEditModal key={col.id} {...row} />;
        }
        if (col.name === "Delete") {
          return <DeleteLaborCodeModal key={col.id} {...row} />;
        }
        if (col.name === "Unit Price") {
          if (
            row.oldPrice &&
            row.oldPrice !== null &&
            row.oldPrice !== "null" &&
            row.oldPrice !== row.price &&
            parseFloat(row.oldPrice).toFixed(2) !== parseFloat(row.price).toFixed(2) &&
            parseFloat(row.price).toFixed(2) !== parseFloat("0").toFixed(2)
          ) {
            return (
              <td
                className={`${tdClass} mobile-data__row__column__item__top__left__name--text customer-txt`}
                key={col.id}
              >
                <CustomTooltip placement="top" trigger="hover" text={`Original Price: ${currencyValue(row.oldPrice)}`}>
                  <span>{currencyValue(row.price)}</span>
                </CustomTooltip>
              </td>
            );
          } else if (row.price && parseFloat(row.price).toFixed(2) === parseFloat("0").toFixed(2)) {
            return (
              <td className={`${tdClass} mobile-data__row__column__item__top__left__name--text price-txt`} key={col.id}>
                <span>{currencyValue(row.price)}</span>
              </td>
            );
          } else {
            return (
              <td className={tdClass} key={col.id}>
                <span>{currencyValue(row.price)}</span>
              </td>
            );
          }
        }
        if (col.name !== "Labor Code") {
          return (
            <td key={col.id} className="table-root-floor__body__tr__td table-root-floor__body__tr__td__all-text">
              {displayRowItem(row, col)}
            </td>
          );
        }
        return null;
      })}
    </tr>
  );
};
